import React from "react";
import PropTypes from "prop-types";
import { DateInput } from "@narmi/design_system";
import { ContextForm } from "cerulean";
import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";

const datesAfterToday = (date) => Date.now() < date;
const DISABLE_DATES = [datesAfterToday];

// Parse a constraint value (minDate, or a disableDates entry) into a DateTime.
// Supports the "M/d/yyyy" strings used by this form as well as Date objects and
// ISO strings, so manual entry honors the same bounds as the picker.
const toDateTime = (val) => {
  if (val instanceof Date) return DateTime.fromJSDate(val);
  if (typeof val === "string") {
    const fromFormat = DateTime.fromFormat(val, "M/d/yyyy");
    return fromFormat.isValid ? fromFormat : DateTime.fromISO(val);
  }
  return DateTime.invalid("unsupported");
};

// Mirror the picker's disableDates/minDate constraints for manually typed dates,
// so a user can't type a disabled (e.g. future) or out-of-range date and slip
// past `required` validation.
const isDateWithinBounds = (parsed, disableDates, minDate) => {
  const jsDate = parsed.toJSDate();

  const disabled = (disableDates || []).some((rule) => {
    if (typeof rule === "function") return rule(jsDate);
    const ruleDate = toDateTime(rule);
    return ruleDate.isValid && ruleDate.hasSame(parsed, "day");
  });
  if (disabled) return false;

  if (minDate) {
    const min = toDateTime(minDate);
    if (min.isValid && parsed.startOf("day") < min.startOf("day")) return false;
  }

  return true;
};

const DateInputWrapper = React.memo(
  ({ onChange, value, allowManualEntry = false, ...rest }) => {
    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;
    const valueRef = React.useRef(value);

    // Keep the current bounds available to the stable onInput callback.
    const boundsRef = React.useRef({});
    boundsRef.current = {
      disableDates: rest.disableDates,
      minDate: rest.minDate,
    };

    const onChangeDate = React.useCallback((val) => {
      valueRef.current = val;
      onChangeRef.current(val);
    }, []);

    // flatpickr's onChange is swallowed on outside click (e.g. clicking
    // Download), so typed dates are committed via onInput. Only complete,
    // in-range dates are committed; partial, invalid, or out-of-range input
    // commits "" so `required` still guards submit and disabled dates can't
    // slip through manual entry.
    const onInputDate = React.useCallback((event) => {
      const raw = (event?.currentTarget?.value ?? "").trim();
      const parsed = DateTime.fromFormat(raw, "M/d/yyyy");
      const { disableDates, minDate } = boundsRef.current;
      const committed =
        parsed.isValid && isDateWithinBounds(parsed, disableDates, minDate)
          ? raw
          : "";
      valueRef.current = committed;
      onChangeRef.current(committed);
    }, []);

    const typeableProps = allowManualEntry
      ? { type: "text", onInput: onInputDate }
      : { altInput: true, altFormat: "m/d/Y" };

    return (
      <DateInput
        {...rest}
        {...typeableProps}
        onChange={onChangeDate}
        defaultDate={valueRef.current}
      />
    );
  },
  (prev, next) =>
    prev.disableDates === next.disableDates &&
    prev.minDate === next.minDate &&
    prev.error === next.error &&
    prev.allowManualEntry === next.allowManualEntry
);

DateInputWrapper.displayName = "DateInputWrapper";
DateInputWrapper.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  allowManualEntry: PropTypes.bool,
};

const DateRangeFormField = ({
  hidden = true,
  required = false,
  minDate = undefined,
  testIdPrefix = "",
  allowManualEntry = false,
}) => {
  const { l10n } = useLocalization();
  if (hidden) return null;

  return (
    <div style={{ display: "flex", marginBottom: "20px" }}>
      <ContextForm.Field style={{ marginBottom: "0px" }} required={required}>
        <DateInputWrapper
          disableDates={DISABLE_DATES}
          allowManualEntry={allowManualEntry}
          dateFormat={"m/d/Y"}
          field="min_date"
          useIsoOnChange={false}
          placeholder="mm/dd/yyyy"
          testId={`${testIdPrefix}-1`}
        />
      </ContextForm.Field>
      <div className="margin--x--xs fontSize--s" style={{ display: "flex" }}>
        <div style={{ margin: "auto" }}>
          {l10n.getString("label-date-range-to")}
        </div>
      </div>
      <ContextForm.Field style={{ marginBottom: "0px" }} required={required}>
        <DateInputWrapper
          disableDates={DISABLE_DATES}
          allowManualEntry={allowManualEntry}
          dateFormat={"m/d/Y"}
          field="max_date"
          minDate={minDate}
          useIsoOnChange={false}
          placeholder="mm/dd/yyyy"
          testId={`${testIdPrefix}-2`}
        />
      </ContextForm.Field>
    </div>
  );
};
DateRangeFormField.propTypes = {
  hidden: PropTypes.bool.isRequired,
  required: PropTypes.bool,
  minDate: PropTypes.string,
  testIdPrefix: PropTypes.string,
  allowManualEntry: PropTypes.bool,
};

export default DateRangeFormField;
